import type { StatBlock, StatId } from '../domain/stats.ts';
import { STAT_IDS } from '../domain/stats.ts';
import type { Roller } from './dice.ts';
import { rollD6RerollingOnes } from './dice.ts';

/**
 * RULE DCC-CREATE-STATS-001 - Standard Array.
 * Source: Crawlers & Customization p.13.
 * Assign the scores 2, 3, 4, 5 and 6, each used exactly once.
 */
export const STANDARD_ARRAY: readonly number[] = [2, 3, 4, 5, 6];

export interface ValidationResult {
  valid: boolean;
  errors: string[];
}

export function validateStandardArray(assignment: Partial<Record<StatId, number>>): ValidationResult {
  const errors: string[] = [];
  const assigned: number[] = [];

  for (const stat of STAT_IDS) {
    const value = assignment[stat];
    if (value === undefined) {
      errors.push(`${stat.toUpperCase()} has not been assigned a score.`);
      continue;
    }
    assigned.push(value);
  }

  if (errors.length === 0) {
    const remaining = [...STANDARD_ARRAY];
    for (const value of assigned) {
      const index = remaining.indexOf(value);
      if (index === -1) {
        errors.push(`The score ${value} is not available in the Standard Array, or was used twice.`);
      } else {
        remaining.splice(index, 1);
      }
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * RULE DCC-CREATE-STATS-002 - Leaving it to Chance.
 * Source: Crawlers & Customization p.14.
 * Starting with Strength and going down the line of Stats, roll 1d6 rerolling
 * any 1s. Scores may not be swapped afterwards.
 */
export function rollStartingStats(roller: Roller): StatBlock {
  return {
    str: rollD6RerollingOnes(roller),
    int: rollD6RerollingOnes(roller),
    con: rollD6RerollingOnes(roller),
    dex: rollD6RerollingOnes(roller),
    cha: rollD6RerollingOnes(roller),
  };
}

/**
 * RULE DCC-CREATE-STATS-003 - At creation the Enhanced and Unenhanced values
 * are the same. Source: Crawlers & Customization p.14.
 * Enhanced Stats are always derived (see rules/enhancedStats.ts), so this
 * helper simply confirms there is nothing extra to store.
 */
export function startingUnenhancedStats(stats: StatBlock): StatBlock {
  return { ...stats };
}
